import { type Box, boundingRect, unionRects } from "@cva/shared";
import { RotateCw } from "lucide-react";
import type { PointerEvent as ReactPointerEvent } from "react";
import { useEditor } from "./store";
import { cursorFor, HANDLE_DIRECTIONS, type Handle, handlesFor } from "./transform";

const HANDLE = 10;

/** Selection frame with resize and rotation handles (constant screen size). */
export function SelectionOverlay({
  boxes,
  zoom,
  onHandle,
  readOnly,
}: {
  boxes: { id: string; box: Box }[];
  zoom: number;
  onHandle: (event: ReactPointerEvent, handle: Handle | "rotate") => void;
  readOnly: boolean;
}) {
  const doc = useEditor((s) => s.doc);
  if (boxes.length === 0) return null;
  const multi = boxes.length > 1;
  const single = boxes[0]!;
  const element = doc.elements[single.id];
  const locked = boxes.some(({ id }) => doc.elements[id]?.locked);
  const frame: Box = multi
    ? { ...unionRects(boxes.map(({ box }) => boundingRect(box)))!, rotation: 0 }
    : single.box;
  const handles = readOnly || locked ? [] : handlesFor(element, multi);
  const size = HANDLE / zoom;
  const border = 1.5 / zoom;

  return (
    <>
      {multi
        ? boxes.map(({ id, box }) => (
            <div
              key={id}
              className="pointer-events-none absolute"
              style={{
                left: box.x,
                top: box.y,
                width: box.width,
                height: box.height,
                transform: box.rotation ? `rotate(${box.rotation}deg)` : undefined,
                outline: `${1 / zoom}px solid rgb(99 102 241 / 0.6)`,
              }}
            />
          ))
        : null}
      <div
        className="pointer-events-none absolute"
        style={{
          left: frame.x,
          top: frame.y,
          width: frame.width,
          height: frame.height,
          transform: frame.rotation ? `rotate(${frame.rotation}deg)` : undefined,
          outline: `${border}px solid ${locked ? "rgb(161 161 170)" : "rgb(79 70 229)"}`,
        }}
      >
        {handles.map((handle) => {
          const { sx, sy } = HANDLE_DIRECTIONS[handle];
          const side = sx === 0 || sy === 0;
          const w = side && sx === 0 ? size * 2.2 : size;
          const h = side && sy === 0 ? size * 2.2 : size;
          // Invisible hit area around the visible handle.
          const hit = size * 1.2;
          return (
            <div
              key={handle}
              data-handle={handle}
              onPointerDown={(event) => onHandle(event, handle)}
              className="pointer-events-auto absolute flex items-center justify-center"
              style={{
                width: w + hit,
                height: h + hit,
                left: `calc(${((sx + 1) / 2) * 100}% - ${(w + hit) / 2}px)`,
                top: `calc(${((sy + 1) / 2) * 100}% - ${(h + hit) / 2}px)`,
                cursor: cursorFor(handle, frame.rotation),
              }}
            >
              <div
                className="rounded-[3px] border-brand-600 bg-white shadow-sm"
                style={{
                  width: w,
                  height: h,
                  borderWidth: border,
                  borderRadius: side ? size : size / 3,
                }}
              />
            </div>
          );
        })}
        {handles.length > 0 ? (
          <div
            data-handle="rotate"
            onPointerDown={(event) => onHandle(event, "rotate")}
            className="pointer-events-auto absolute flex items-center justify-center rounded-full border-zinc-300 bg-white text-zinc-700 shadow"
            title="Drehen (Shift: 15°-Schritte)"
            style={{
              width: size * 2.2,
              height: size * 2.2,
              left: `calc(50% - ${size * 1.1}px)`,
              top: -size * 4.2,
              borderWidth: 1 / zoom,
              cursor: "grab",
            }}
          >
            <RotateCw style={{ width: size * 1.3, height: size * 1.3 }} />
          </div>
        ) : null}
      </div>
    </>
  );
}
